import React from "react";
import { Eye, Trash2, KeyRound, Mail, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { User } from "../../types";
import ActionMenu, { ActionMenuItem } from "../UI/ActionMenu";

interface UserTableProps {
  users: User[];
  selectedIds: string[];
  onSelectAll: (checked: boolean) => void;
  onSelectOne: (id: string, checked: boolean) => void;
  onEdit: (user: User) => void;
  onDelete: (id: string) => void;
  loading: boolean;
  isUsersPage?: boolean;
  /**
   * ── The three PIN actions, each independently opt-in ────────────────────────
   *
   * This table is shared: the Users page renders admins and dispatchers through
   * it, and the Drivers page renders drivers. A truck PIN is meaningless for the
   * first group — they have no cab screen to sign in to — so none of this may be
   * unconditional.
   *
   * Each callback is optional and each gates only its own menu entry, and the
   * *column* appears only if at least one of them is supplied. A caller that
   * passes nothing gets exactly the table it had before any of this existed,
   * which is what the Users page relies on.
   *
   * Three separate callbacks rather than one with a mode, because the three do
   * genuinely different things and one of them is destructive:
   *
   *   onSendPin    email the driver the PIN they already have — changes nothing
   *   onViewPin    read it back on screen, for a driver on the phone
   *   onIssuePin   replace it with a new one — the old one stops working
   */
  onSendPin?: (user: User) => void;
  onViewPin?: (user: User) => void;
  onIssuePin?: (user: User) => void;
}

export const UserTable: React.FC<UserTableProps> = ({
  users,
  onEdit,
  onDelete,
  loading,
  isUsersPage = false,
  onSendPin,
  onViewPin,
  onIssuePin,
}) => {
  const { t } = useTranslation("users");

  /**
   * Whether this table is showing PINs at all.
   *
   * One test for the column and for the menu block, so a caller that wires up
   * only some of the actions never gets a column with nothing under it, and the
   * Users page — which wires up none — is untouched.
   */
  const showsPins = Boolean(onSendPin || onViewPin || onIssuePin);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    const colors = {
      active: "bg-green-100 text-green-800",
      inactive: "bg-red-100 text-red-800",
      pending: "bg-yellow-100 text-yellow-800",
      other: "bg-gray-100 text-gray-800",
    };
    return colors[status as keyof typeof colors] || colors.other;
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-12 bg-gray-100 border-b border-gray-200"></div>
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="h-16 bg-gray-50 border-b border-gray-100"
          ></div>
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto max-h-[50svh] md:max-h-[55vh] lg:max-h-[60vh] xl:max-h-[70vh] border-b border-b-gray-300">
      <table className="min-w-full border-collapse border border-gray-300">
        <thead className="bg-gray-50 text-nowrap sticky top-0 z-1">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("table.columns.id")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("table.columns.name")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("table.columns.email")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("table.columns.phone")}
            </th>
            {isUsersPage && (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
                {t("table.columns.role")}
              </th>
            )}
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("table.columns.status")}
            </th>
            {showsPins && (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
                {t("table.columns.truckPin")}
              </th>
            )}
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("table.columns.created")}
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
              {t("table.columns.actions")}
            </th>
          </tr>
        </thead>

        <tbody className="bg-white">
          {users.map((user) => (
            <tr
              key={user._id}
              className="hover:bg-gray-50 transition-colors duration-150"
            >
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium border border-gray-200">
                {user._id.slice(-6).toUpperCase()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-500 border border-gray-200">
                {user.fullName}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                {user.email}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                {user.phone || t("table.cell.noData")}
              </td>
              {isUsersPage && (
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                  {user.role || t("table.cell.noData")}
                </td>
              )}
              <td className="px-6 py-4 whitespace-nowrap border border-gray-200">
                <span
                  className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                    user.status,
                  )}`}
                >
                  {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                </span>
              </td>
              {/*
                Whether this driver can sign in on a truck at all.

                Called out as its own column rather than buried in the row menu
                because the question it answers is a list-wide one: after truck
                devices shipped, every driver created beforehand has no PIN, and
                an admin needs to see who at a glance rather than opening forty
                menus to find out.
              */}
              {showsPins && (
                <td className="px-6 py-4 whitespace-nowrap border border-gray-200">
                  {user.hasDriverPin ? (
                    <span className="inline-flex items-center gap-1 text-sm text-gray-500">
                      <KeyRound className="w-3.5 h-3.5 text-green-600" />
                      {t("table.cell.pinIssued")}
                    </span>
                  ) : (
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                      {t("table.cell.noPin")}
                    </span>
                  )}
                </td>
              )}
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                {formatDate(user.createdAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium border border-gray-200">
                <div className="flex items-center justify-end">
                  <ActionMenu
                    ariaLabel={t("table.columns.actions")}
                    items={
                      [
                        {
                          label: t("table.actions.edit"),
                          icon: <Eye className="w-4 h-4" />,
                          onClick: () => onEdit(user),
                        },
                        /*
                          The menu adapts to what this driver actually has.

                          With no PIN there is one sensible act — send them one —
                          and offering "Show PIN" or "Issue a new PIN" beside it
                          would be two entries that can only explain themselves.
                          Once they have one, all three make sense, and "Show"
                          appears only if this particular PIN is readable: one
                          issued before readable copies existed works fine and
                          cannot be displayed.
                        */
                        ...(onSendPin
                          ? [
                              {
                                label: user.hasDriverPin
                                  ? t("table.actions.sendPin")
                                  : t("table.actions.sendFirstPin"),
                                icon: <Mail className="w-4 h-4" />,
                                onClick: () => onSendPin(user),
                              },
                            ]
                          : []),
                        /*
                          Offered whenever the driver has a PIN — not only when
                          that PIN happens to be readable.

                          Gating on `driverPinViewable` hid the action from every
                          driver whose PIN predates readable copies, which is all
                          of them immediately after this shipped: the feature
                          looked as though it had never been built, and the one
                          route to making a PIN viewable (issuing a new one) was
                          behind the entry that was hidden. A capability that
                          disappears exactly when it is needed is worse than one
                          that explains itself, so the explaining is done in the
                          dialog, which also offers the fix.
                        */
                        ...(onViewPin && user.hasDriverPin
                          ? [
                              {
                                label: t("table.actions.viewPin"),
                                icon: <KeyRound className="w-4 h-4" />,
                                onClick: () => onViewPin(user),
                              },
                            ]
                          : []),
                        ...(onIssuePin && user.hasDriverPin
                          ? [
                              {
                                // Destructive: the driver's current PIN stops
                                // working the moment this succeeds.
                                label: t("table.actions.reissuePin"),
                                icon: <RefreshCw className="w-4 h-4" />,
                                onClick: () => onIssuePin(user),
                              },
                            ]
                          : []),
                        {
                          label: t("table.actions.delete"),
                          icon: <Trash2 className="w-4 h-4" />,
                          onClick: () => onDelete(user._id),
                          variant: "danger",
                          divider: true,
                        },
                      ] as ActionMenuItem[]
                    }
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {users.length === 0 && !loading && (
        <div className="text-center py-12">
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t("table.empty.title")}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t("table.empty.description")}
          </p>
        </div>
      )}
    </div>
  );
};
